import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, TextField, Button, Chip,
  CircularProgress, Alert, Paper, LinearProgress, Divider,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../services/api';

const QUOTA = 100;

const cell = { borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' };
const labelCell = { ...cell, color: '#666', fontSize: 12 };

export default function AdminDashboardPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers]           = useState([]);
  const [status, setStatus]         = useState(null);
  const [selected, setSelected]     = useState(new Set());
  const [search, setSearch]         = useState('');
  const [saving, setSaving]         = useState(false);
  const [loadError, setLoadError]   = useState('');
  const [saveMsg, setSaveMsg]       = useState('');
  const [dataLoading, setDataLoading] = useState(true);

  // Redirect non-admins
  useEffect(() => {
    if (!authLoading && !isAdmin) navigate('/', { replace: true });
  }, [authLoading, isAdmin, navigate]);

  const loadData = useCallback(async () => {
    setDataLoading(true);
    setLoadError('');
    try {
      const [usersRes, statusRes] = await Promise.all([
        adminAPI.getUsers(),
        adminAPI.getBriefingStatus(),
      ]);
      const usersData = usersRes.data?.data ?? [];
      setUsers(usersData);
      setSelected(new Set(
        usersData.filter(u => u.briefingSelected).map(u => u.id)
      ));
      setStatus(statusRes.data?.data ?? null);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message;
      console.error('Admin data load failed:', status, msg, err);
      setLoadError(`Failed to load admin data (${status ?? 'network error'}: ${msg ?? 'unknown'}).`);
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) loadData(); }, [isAdmin, loadData]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < QUOTA) next.add(id);
      return next;
    });
    setSaveMsg('');
  };

  const saveSelection = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      await adminAPI.updateBriefingSelection([...selected]);
      setSaveMsg('Selection saved — briefing will send tomorrow at 7:00 AM UTC.');
      const statusRes = await adminAPI.getBriefingStatus();
      setStatus(statusRes.data.data);
    } catch {
      setSaveMsg('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  if (authLoading || dataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#fff' }} />
      </Box>
    );
  }

  if (!isAdmin) return null;

  const quotaUsed = selected.size;
  const quotaPct  = (quotaUsed / QUOTA) * 100;
  const quotaColor = quotaPct >= 90 ? '#ef5350' : quotaPct >= 70 ? '#ffa726' : '#66bb6a';

  const formatDate = (iso) => iso
    ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="overline" sx={{ color: '#555', letterSpacing: 4, fontSize: 11 }}>
          ADMIN
        </Typography>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mt: 0.5 }}>
          Daily Briefing Dashboard
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
          Select users to receive the daily space weather email every morning at 7:00 AM UTC.
        </Typography>
      </Box>

      {loadError && <Alert severity="error" sx={{ mb: 3 }}>{loadError}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 300px' }, gap: 3, alignItems: 'start' }}>

        {/* ── Left: User Table ── */}
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                },
                '& input::placeholder': { color: '#555' },
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const allSelected = filtered.every(u => selected.has(u.id));
                if (allSelected) {
                  setSelected(prev => {
                    const next = new Set(prev);
                    filtered.forEach(u => next.delete(u.id));
                    return next;
                  });
                } else {
                  setSelected(prev => {
                    const next = new Set(prev);
                    for (const u of filtered) {
                      if (next.size >= QUOTA) break;
                      next.add(u.id);
                    }
                    return next;
                  });
                }
                setSaveMsg('');
              }}
              sx={{
                whiteSpace: 'nowrap',
                borderColor: 'rgba(255,255,255,0.2)',
                color: '#aaa',
                fontSize: 10,
                letterSpacing: 1.5,
                fontWeight: 600,
                borderRadius: 0,
                px: 2,
                '&:hover': { borderColor: '#fff', color: '#fff' },
              }}
            >
              {filtered.length > 0 && filtered.every(u => selected.has(u.id)) ? 'DESELECT ALL' : 'SELECT ALL'}
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ background: '#0D0D0D', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...cell, width: 48, color: '#555', fontSize: 11 }}>
                    SEND
                  </TableCell>
                  <TableCell sx={{ ...cell, color: '#555', fontSize: 11 }}>NAME</TableCell>
                  <TableCell sx={{ ...cell, color: '#555', fontSize: 11 }}>EMAIL</TableCell>
                  <TableCell sx={{ ...cell, color: '#555', fontSize: 11 }}>TYPE</TableCell>
                  <TableCell sx={{ ...cell, color: '#555', fontSize: 11 }}>STATUS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ ...cell, textAlign: 'center', color: '#555', py: 4 }}>
                      No users found
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map(u => {
                  const isChecked = selected.has(u.id);
                  const isDisabled = !isChecked && selected.size >= QUOTA;
                  return (
                    <TableRow
                      key={u.id}
                      hover
                      onClick={() => !isDisabled && toggle(u.id)}
                      sx={{
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.45 : 1,
                        '&:hover': { background: 'rgba(255,255,255,0.03)' },
                        background: isChecked ? 'rgba(255,255,255,0.04)' : 'transparent',
                      }}
                    >
                      <TableCell sx={cell} onClick={e => e.stopPropagation()}>
                        <Checkbox
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={() => toggle(u.id)}
                          size="small"
                          sx={{ color: '#444', '&.Mui-checked': { color: '#fff' }, p: 0 }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...cell, fontWeight: 500 }}>{u.name}</TableCell>
                      <TableCell sx={{ ...cell, color: '#888', fontSize: 12 }}>{u.email}</TableCell>
                      <TableCell sx={cell}>
                        <Chip
                          label={u.accountType}
                          size="small"
                          sx={{
                            height: 20, fontSize: 10, letterSpacing: 1,
                            background: u.accountType === 'PRO' ? 'rgba(206,147,216,0.15)' : 'rgba(255,255,255,0.07)',
                            color: u.accountType === 'PRO' ? '#ce93d8' : '#888',
                            border: '1px solid',
                            borderColor: u.accountType === 'PRO' ? 'rgba(206,147,216,0.3)' : 'rgba(255,255,255,0.1)',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={cell}>
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: u.isActive ? '#66bb6a' : '#555',
                          display: 'inline-block',
                        }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* ── Right: Status Panel ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Quota */}
          <Box sx={{ background: '#0D0D0D', border: '1px solid rgba(255,255,255,0.08)', p: 3 }}>
            <Typography sx={{ color: '#555', fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', mb: 2 }}>
              Daily Quota
            </Typography>
            <Typography sx={{ color: '#fff', fontSize: 28, fontWeight: 700, lineHeight: 1 }}>
              {quotaUsed}
              <Typography component="span" sx={{ color: '#555', fontSize: 16, fontWeight: 400 }}>
                /{QUOTA}
              </Typography>
            </Typography>
            <Typography sx={{ color: '#555', fontSize: 11, mt: 0.5, mb: 2 }}>users selected</Typography>
            <LinearProgress
              variant="determinate"
              value={quotaPct}
              sx={{
                height: 3, borderRadius: 0, background: 'rgba(255,255,255,0.08)',
                '& .MuiLinearProgress-bar': { background: quotaColor, borderRadius: 0 },
              }}
            />
            {quotaUsed >= QUOTA && (
              <Typography sx={{ color: '#ffa726', fontSize: 11, mt: 1 }}>
                Quota full — deselect a user to add another.
              </Typography>
            )}
          </Box>

          {/* Send History */}
          {status && (
            <Box sx={{ background: '#0D0D0D', border: '1px solid rgba(255,255,255,0.08)', p: 3 }}>
              <Typography sx={{ color: '#555', fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', mb: 2 }}>
                Send History
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  ['Last Sent', formatDate(status.lastSentAt)],
                  ['Total Delivered', status.totalSent],
                  ['Total Failed', status.totalFailed],
                ].map(([label, val]) => (
                  <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: '#555', fontSize: 12 }}>{label}</Typography>
                    <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 500 }}>{val}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Save */}
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
          {saveMsg && (
            <Alert
              severity={saveMsg.includes('failed') ? 'error' : 'success'}
              sx={{ fontSize: 12, py: 0.5 }}
            >
              {saveMsg}
            </Alert>
          )}
          <Button
            variant="outlined"
            fullWidth
            disabled={saving}
            onClick={saveSelection}
            sx={{
              borderColor: 'rgba(255,255,255,0.3)',
              color: '#fff',
              borderRadius: 0,
              letterSpacing: 2,
              fontSize: 11,
              fontWeight: 600,
              py: 1.5,
              '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.05)' },
              '&.Mui-disabled': { color: '#555', borderColor: '#333' },
            }}
          >
            {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'SAVE SELECTION'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
